import { z } from 'zod';
import {
  CatalogAssemblySchema,
  CatalogItemSchema,
  catalogTitleKey,
  markupForLine,
  type CatalogAssembly,
  type CatalogItem,
} from '../../domain/entities/catalog.ts';
import {
  MAX_MARKUP_MILLIONTHS,
  type CostCode,
  type CostType,
} from '../../domain/entities/cost-code.ts';
import { newId } from '../../domain/id.ts';
import { vendorNumberClashes } from './item-vendors.ts';
import type { Money } from '../../domain/money.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Managing the catalog, and turning its items into lines.
 *
 * `catalogLinesFrom` at the bottom is the part every later module uses:
 * `Add From Catalog` on an estimate, a change order, a purchase order and a
 * bill all mean the same thing, and they should all mean it the same way.
 * Writing that conversion four times is how three of them end up using the
 * cost code's markup on an item somebody set to pass through at cost.
 */
export class CatalogError extends Error {
  readonly reason: 'invalid' | 'unknownItem';

  constructor(reason: 'invalid' | 'unknownItem', message: string) {
    super(message);
    this.name = 'CatalogError';
    this.reason = reason;
  }
}

export const SaveCatalogItemInputSchema = z.object({
  title: z.string().trim().min(1, 'A catalog item needs a title').max(200),
  description: z.string().trim().max(2_000).optional(),
  costCodeId: z.string().min(1, 'Pick a cost code'),
  costType: z.custom<CostType>().optional(),
  unitCost: z.custom<Money>(),
  unit: z.string().trim().max(20).optional(),
  /**
   * Absent means "use the cost code's default"; zero means "pass this
   * through at cost". The two are different and `markupForLine` is where
   * that matters — see the note there.
   */
  markupMillionths: z.number().int().min(0).max(MAX_MARKUP_MILLIONTHS).optional(),
  vendorContactId: z.string().min(1).optional(),
  /**
   * Everyone this part can be bought from (SCRUM-284).
   *
   * Replaces the pair above, which is kept for items written before it
   * existed. A form that sends this takes over completely — see
   * `itemVendors`.
   */
  vendors: z
    .array(
      z.object({
        vendorContactId: z.string().min(1),
        vendorItemNumber: z.string().trim().max(80).optional(),
      }),
    )
    .max(50)
    .readonly()
    .optional(),
  /**
   * Where it lives, and whereabouts there (INV2-06, SCRUM-268).
   *
   * All three optional and all three clearable: an item that moves out
   * of a rack has to be able to say so, which is why they are spelled
   * out in the patch below rather than merged.
   */
  locationId: z.string().min(1).optional(),
  rack: z.string().trim().max(20).optional(),
  bin: z.string().trim().max(20).optional(),
  imageFileId: z.string().min(1).optional(),
  active: z.boolean().optional(),
  tagIds: z.array(z.string().min(1)).optional(),
});
export type SaveCatalogItemInput = z.input<typeof SaveCatalogItemInputSchema>;

export const SaveAssemblyInputSchema = z.object({
  title: z.string().trim().min(1, 'An assembly needs a title').max(200),
  description: z.string().trim().max(2_000).optional(),
  lines: z
    .array(
      z.object({
        catalogItemId: z.string().min(1),
        quantityThousandths: z.number().int().positive(),
      }),
    )
    .optional(),
  active: z.boolean().optional(),
  tagIds: z.array(z.string().min(1)).optional(),
});
export type SaveAssemblyInput = z.input<typeof SaveAssemblyInputSchema>;

export interface CatalogContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

type ReadContext = Pick<CatalogContext, 'data' | 'companyId'>;

function itemRepo(context: ReadContext) {
  return context.data.collection<CatalogItem>({ companyId: context.companyId }, 'catalogItems');
}

function assemblyRepo(context: ReadContext) {
  return context.data.collection<CatalogAssembly>(
    { companyId: context.companyId },
    'catalogAssemblies',
  );
}

export async function listCatalogItems(context: ReadContext): Promise<CatalogItem[]> {
  const page = await itemRepo(context).list({ orderBy: [{ field: 'title', direction: 'asc' }] });
  return [...page.items];
}

export function subscribeToCatalogItems(
  context: ReadContext,
  onChange: (items: readonly CatalogItem[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return itemRepo(context).subscribe(
    { orderBy: [{ field: 'title', direction: 'asc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

export async function listCatalogAssemblies(context: ReadContext): Promise<CatalogAssembly[]> {
  const page = await assemblyRepo(context).list({
    orderBy: [{ field: 'title', direction: 'asc' }],
  });
  return [...page.items];
}

export function subscribeToCatalogAssemblies(
  context: ReadContext,
  onChange: (assemblies: readonly CatalogAssembly[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return assemblyRepo(context).subscribe(
    { orderBy: [{ field: 'title', direction: 'asc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

/**
 * Items whose title clashes with this one.
 *
 * Returned for the caller to *warn* with, not to refuse on. Two items
 * called "Stud" at different lengths is a real thing a company has, and
 * refusing the second would be the tool arguing with its user — unlike a
 * cost code number, which is an identifier and must be unique.
 */
export async function catalogTitleClashes(
  context: ReadContext,
  title: string,
  exceptId?: string,
): Promise<CatalogItem[]> {
  const key = catalogTitleKey(title);
  const items = await listCatalogItems(context);
  return items.filter((item) => item.id !== exceptId && catalogTitleKey(item.title) === key);
}

/**
 * Refuse a part number a vendor already uses for something else
 * (SCRUM-284).
 *
 * The one rule here that spans documents: one vendor may not use one
 * part number for **two different SKUs**, because a number scanned off
 * a box would then be ambiguous. Two different vendors sharing a number
 * is fine and common, and the shape of the list already makes a
 * duplicate within one item impossible.
 *
 * **A refusal, not a warning**, unlike `catalogTitleClashes` just
 * above: two items called "Mounting bracket" is a real thing a company
 * has, and an ambiguous part number is not — it is a wrong box arriving
 * on site.
 *
 * Checked over the catalog rather than in a transaction, which is worth
 * naming: two people adding the same number at the same moment could
 * both pass. The airtight version is a callable holding a
 * `vendor:number` index, like the SKU counter — worth having when a
 * second person edits the catalog at the same time as the first, and
 * not before.
 */
async function refuseClashingVendorNumbers(
  context: CatalogContext,
  vendors: readonly { vendorContactId: string; vendorItemNumber?: string }[] | undefined,
  exceptItemId?: string,
): Promise<void> {
  const numbered = (vendors ?? []).filter((one) => one.vendorItemNumber);
  if (numbered.length === 0) return;

  const items = await listCatalogItems(context);
  for (const link of numbered) {
    const clash = vendorNumberClashes(items, link, exceptItemId)[0];
    if (clash) {
      throw new CatalogError(
        'invalid',
        `That vendor already uses ${link.vendorItemNumber} for “${clash.title}”. ` +
          'One part number cannot mean two different things to the same supplier.',
      );
    }
  }
}

export async function createCatalogItem(
  context: CatalogContext,
  input: SaveCatalogItemInput,
): Promise<CatalogItem> {
  const parsed = SaveCatalogItemInputSchema.safeParse(input);
  if (!parsed.success) throw new CatalogError('invalid', parsed.error.issues[0]!.message);

  await refuseClashingVendorNumbers(context, parsed.data.vendors);

  const now = context.clock.now().toISOString();
  const checked = CatalogItemSchema.safeParse({
    id: newId(),
    companyId: context.companyId,
    ...parsed.data,
    active: parsed.data.active ?? true,
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  });
  if (!checked.success) throw new CatalogError('invalid', checked.error.issues[0]!.message);

  await itemRepo(context).create(checked.data);
  return checked.data;
}

export async function saveCatalogItem(
  context: CatalogContext,
  id: string,
  input: SaveCatalogItemInput,
): Promise<CatalogItem> {
  const parsed = SaveCatalogItemInputSchema.safeParse(input);
  if (!parsed.success) throw new CatalogError('invalid', parsed.error.issues[0]!.message);

  const existing = await itemRepo(context).get(id);
  if (!existing) throw new NotFoundError('catalogItems', id);

  await refuseClashingVendorNumbers(context, parsed.data.vendors, id);

  // Spread explicitly rather than `{...existing, ...parsed.data}`, because
  // `markupMillionths: undefined` has to be able to *clear* the field —
  // "use the cost code's default" is a state somebody can go back to, and
  // a merge that skipped undefined would make it one-way.
  const patch = {
    title: parsed.data.title,
    description: parsed.data.description ?? existing.description,
    costCodeId: parsed.data.costCodeId,
    costType: parsed.data.costType,
    unitCost: parsed.data.unitCost,
    unit: parsed.data.unit,
    markupMillionths: parsed.data.markupMillionths,
    vendorContactId: parsed.data.vendorContactId,
    vendors: parsed.data.vendors,
    locationId: parsed.data.locationId,
    rack: parsed.data.rack,
    bin: parsed.data.bin,
    imageFileId: parsed.data.imageFileId,
    active: parsed.data.active ?? existing.active,
    tagIds: parsed.data.tagIds ?? existing.tagIds,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const checked = CatalogItemSchema.safeParse({ ...existing, ...patch });
  if (!checked.success) throw new CatalogError('invalid', checked.error.issues[0]!.message);

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId },
      collection: 'catalogItems' as const,
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return checked.data;
}

/**
 * Stop offering an item.
 *
 * Retired rather than deleted, like a cost code — but for a weaker reason,
 * worth being honest about. Nothing points at a catalog item once a line
 * has been made from it: the line holds a copy of the title, cost and
 * markup, because what a job was priced at must not change when the
 * catalog does. So deleting an item would not break any history.
 *
 * It is still retirement rather than deletion, because **an assembly does**
 * point at items, and a deleted item leaves a bundle that prices a job
 * short. `resolveAssembly` shows such a line as missing; retiring means
 * that state is rare rather than routine.
 */
export async function retireCatalogItem(context: CatalogContext, id: string): Promise<CatalogItem> {
  const existing = await itemRepo(context).get(id);
  if (!existing) throw new NotFoundError('catalogItems', id);
  if (!existing.active) return existing;
  return saveCatalogItem(context, id, { ...existing, active: false });
}

export async function createAssembly(
  context: CatalogContext,
  input: SaveAssemblyInput,
): Promise<CatalogAssembly> {
  const parsed = SaveAssemblyInputSchema.safeParse(input);
  if (!parsed.success) throw new CatalogError('invalid', parsed.error.issues[0]!.message);

  const now = context.clock.now().toISOString();
  const checked = CatalogAssemblySchema.safeParse({
    id: newId(),
    companyId: context.companyId,
    ...parsed.data,
    active: parsed.data.active ?? true,
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  });
  if (!checked.success) throw new CatalogError('invalid', checked.error.issues[0]!.message);

  await assemblyRepo(context).create(checked.data);
  return checked.data;
}

export async function saveAssembly(
  context: CatalogContext,
  id: string,
  input: SaveAssemblyInput,
): Promise<CatalogAssembly> {
  const parsed = SaveAssemblyInputSchema.safeParse(input);
  if (!parsed.success) throw new CatalogError('invalid', parsed.error.issues[0]!.message);

  const existing = await assemblyRepo(context).get(id);
  if (!existing) throw new NotFoundError('catalogAssemblies', id);

  const patch = {
    title: parsed.data.title,
    description: parsed.data.description ?? existing.description,
    lines: parsed.data.lines ?? existing.lines,
    active: parsed.data.active ?? existing.active,
    tagIds: parsed.data.tagIds ?? existing.tagIds,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const checked = CatalogAssemblySchema.safeParse({ ...existing, ...patch });
  if (!checked.success) throw new CatalogError('invalid', checked.error.issues[0]!.message);

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId },
      collection: 'catalogAssemblies' as const,
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return checked.data;
}

/**
 * A line as a catalog item would make one.
 *
 * Shaped like `CoLine` because that is what exists today; Phase 5's
 * `CostLine` (P5-04) will be the same fields and this is the one place that
 * has to change when it arrives.
 */
export interface CatalogLine {
  readonly id: string;
  readonly description: string;
  readonly quantityThousandths: number;
  readonly unit?: string;
  readonly unitCost: Money;
  readonly markupMillionths: number;
  readonly costCodeId: string;
  readonly catalogItemId: string;
}

/**
 * Turn catalog picks into lines — the whole point of `Add From Catalog`.
 *
 * **In one place, used by every module that has a line-item table.** An
 * estimate, a change order, a purchase order and a bill all mean the same
 * thing by "add from catalog", and four implementations is three chances to
 * get `markupForLine` wrong.
 *
 * The values are **copied**, not referenced. What a job was priced at must
 * not change because somebody corrected the catalog afterwards — a
 * reference would rewrite last year's estimates to disagree with last
 * year's bills. `catalogItemId` is kept so a screen can say where the line
 * came from, and is a provenance note rather than a live link.
 */
export function catalogLinesFrom(
  picks: readonly { readonly itemId: string; readonly quantityThousandths: number }[],
  items: readonly CatalogItem[],
  codes: readonly Pick<CostCode, 'id' | 'defaultMarkupMillionths'>[],
  makeId: () => string = newId,
): readonly CatalogLine[] {
  const itemsById = new Map(items.map((item) => [item.id, item] as const));
  const codesById = new Map(codes.map((code) => [code.id, code] as const));

  return picks.flatMap((pick) => {
    const item = itemsById.get(pick.itemId);
    // A pick whose item has gone is skipped rather than turned into an
    // empty line. An empty line on an estimate is a line somebody has to
    // notice and delete; a missing one is visible as the thing they tried
    // to add not appearing.
    if (!item) return [];
    return [
      {
        id: makeId(),
        description: item.title,
        quantityThousandths: pick.quantityThousandths,
        ...(item.unit ? { unit: item.unit } : {}),
        unitCost: item.unitCost,
        markupMillionths: markupForLine(item, codesById.get(item.costCodeId) ?? null),
        costCodeId: item.costCodeId,
        catalogItemId: item.id,
      },
    ];
  });
}

/**
 * The picks an assembly implies, with its quantities multiplied through.
 *
 * Adding two of "exterior wall, per foot" where the wall uses 1.5 studs
 * means three studs. Thousandths throughout, so the multiplication is
 * integer and `2 × 1.5` is exactly `3` rather than `2.9999999999999996`.
 */
export function assemblyPicks(
  assembly: Pick<CatalogAssembly, 'lines'>,
  quantityThousandths: number,
): readonly { readonly itemId: string; readonly quantityThousandths: number }[] {
  return assembly.lines.map((line) => ({
    itemId: line.catalogItemId,
    // Both are thousandths, so the product is millionths — divided back
    // once, here, rather than compounding a rounding error per line.
    quantityThousandths: Math.round((line.quantityThousandths * quantityThousandths) / 1000),
  }));
}
